import { isAceleresIndividualOwn } from 'dashboard/constants/aceleresConnectionTypes';
import { isGroupConversation } from 'dashboard/components-next/message/groupParticipant';

/**
 * Espelha `ConversationFinder#group_conversation_sql`: o marcador
 * `is_whatsapp_group` vem da ponte WAME atual e o identificador
 * `group:<jid>@g.us` existe desde sempre — nenhuma das duas fontes cobre o
 * histórico inteiro sozinha.
 */
export const isAceleresGroupConversation = conversation => {
  if (isGroupConversation(conversation)) return true;

  const identifier = String(conversation?.meta?.sender?.identifier || '');
  return identifier.endsWith('@g.us');
};

const NON_STARTED_LOCK_REASONS = new Set([
  'carteirizado',
  'carteira_reservada',
  'manual_assign',
  'max_cycles_exhausted',
  'manual_assignment_after_exhaustion',
]);

const isTruthy = value =>
  value === true || value === 'true' || value === 1 || value === '1';

export const isAceleresConversationStarted = conversation => {
  const attributes = conversation?.additional_attributes || {};
  const firstReplyCreatedAt = conversation?.first_reply_created_at;

  if (Object.hasOwn(attributes, 'attendance_started')) {
    return isTruthy(attributes.attendance_started);
  }

  if (
    firstReplyCreatedAt &&
    firstReplyCreatedAt !== 0 &&
    firstReplyCreatedAt !== '0'
  ) {
    return true;
  }

  if (!isTruthy(attributes.rodizio_locked)) {
    return false;
  }

  return !NON_STARTED_LOCK_REASONS.has(
    String(attributes.rodizio_locked_reason || '')
  );
};

export const isAceleresPersonalConversation = ({
  conversation = {},
  inbox = {},
} = {}) => {
  const conversationAttributes = conversation.additional_attributes || {};
  const inboxAttributes =
    inbox.additional_attributes ||
    conversation.inbox?.additional_attributes ||
    {};

  return (
    isAceleresIndividualOwn(inboxAttributes.connection_type) ||
    Number(conversationAttributes.personal_owner_chatwoot_user_id) > 0 ||
    Number(conversationAttributes.personal_origin_chatwoot_user_id) > 0 ||
    Number(conversationAttributes.owner_chatwoot_user_id) > 0
  );
};

/**
 * Aceleres 4 caixas: gate do "Iniciar atendimento" — conversa atribuída a mim
 * e ainda NÃO iniciada, ou conversa aberta SEM responsável, mostra o botão.
 * Conversa pessoal nunca entra no gate: ela mora na caixa Pessoais, que por
 * regra não tem assignee nem fluxo de início de atendimento.
 *
 * A regra vivia dentro do ReplyBox. Passou a morar aqui quando o modal de
 * dados do contato precisou da mesma resposta — assim as duas telas nunca
 * divergem sobre o que é um atendimento já iniciado.
 *
 * A condição espelha `Aceleres::OutboundSendGate#reply_blocked?` (Rails), e
 * precisa continuar espelhando: enquanto a tela perguntava só "está
 * iniciada?" e o servidor perguntava "tem responsável?", a conversa iniciada
 * E sem responsável virava um beco sem saída — o POST voltava 423 pedindo
 * "Clique em Iniciar atendimento" e o botão não era desenhado, porque a
 * conversa já constava iniciada. Estado real e comum: quem responde pelo
 * celular marca o atendimento como iniciado (Aceleres::AttendanceMarker) e o
 * âncora do rodízio não grava responsável quando quem respondeu é gestor
 * (99_aceleres_rodizio_anchor.rb) — ninguém, nem o dono do número, conseguia
 * mais escrever ali (produção, conversa 40 da conta 922, 08/09/2026).
 */
export const aceleresNeedsStartGate = ({
  conversation,
  inbox = {},
  currentUser = {},
} = {}) => {
  if (!conversation || !conversation.id) return false;

  const me = currentUser && currentUser.id;
  const assigneeId =
    conversation.meta?.assignee?.id ?? conversation.assignee_id;
  const isMine = Boolean(me) && Number(assigneeId) === Number(me);
  const isUnassigned = !assigneeId;
  const isPersonal = isAceleresPersonalConversation({ conversation, inbox });
  const started = isAceleresConversationStarted(conversation);

  // Grupo de instância compartilhada não tem atendimento a iniciar: ele é
  // coletivo e já nasce respondível por quem enxerga o canal. Como grupo mora
  // permanentemente em NOVOS e nunca ganha responsável, o ramo "sem dono" abaixo
  // casava sempre e trocava o composer pelo portão de "Iniciar atendimento" —
  // o mesmo defeito que já apareceu no painel do Laravel
  // (`conversationRequiresStart`), agora do lado do Zoraxy. E era esse botão que
  // gravava assignee no grupo, jogando-o para MEUS até a próxima mensagem
  // desatribuí-lo de volta.
  //
  // Em conexão pessoal o grupo é do dono e continua caindo em `isPersonal`.
  if (isAceleresGroupConversation(conversation)) return false;

  // `status` só restringe o ramo de conversa sem responsável porque é só nele
  // que o servidor condiciona o bloqueio a `conversation.open?`; o ramo "minha
  // e não iniciada" segue valendo em qualquer estado, como sempre valeu.
  const isOpen = String(conversation.status || 'open') === 'open';

  return (
    !isPersonal &&
    ((isMine && !started) ||
      (isUnassigned && isOpen))
  );
};

/**
 * A inbox é uma conexão pessoal (individual_own) DESTE usuário.
 *
 * `isAceleresPersonalConversation` acima responde uma pergunta diferente e
 * mais larga — "isto é uma conversa pessoal de alguém?" — e por isso serve ao
 * gate de início de atendimento. Aqui a pergunta tem dono: só quem é titular
 * da conexão enxerga a conversa na caixa Pessoais.
 */
export const isAceleresPersonalInboxOwnedBy = (inbox, userId) => {
  const uid = Number(userId || 0);
  if (!uid) return false;

  const attributes =
    inbox?.additional_attributes || inbox?.additionalAttributes || {};

  return (
    isAceleresIndividualOwn(attributes.connection_type) &&
    Number(attributes.owner_chatwoot_user_id) === uid
  );
};

/**
 * A conversa está na caixa Pessoais DESTE usuário.
 *
 * Espelha `ConversationFinder#personal_scope` (Rails): a conversa sai de
 * Pessoais exatamente quando entra em Meus — atribuída a este usuário E
 * iniciada. Só um dos dois não basta: atribuída e não iniciada não cabe em
 * Meus e precisa continuar visível aqui.
 *
 * A regra nasceu dentro do ChatList e passou a morar aqui quando a bolha de
 * mensagem precisou da mesma resposta para esconder o nome do vendedor. Ela
 * não pode existir em dois lugares: a lista e o chat discordarem sobre o que
 * é uma conversa de Pessoais significa esconder o nome numa conversa que a
 * lista mostra em Meus — ou o contrário.
 *
 * @param {object}  params
 * @param {object}  params.conversation
 * @param {object}  [params.inbox]              inbox da conversa
 * @param {number}  params.currentUserId
 * @param {boolean} [params.ownsPersonalInbox]  atalho para quem já tem a lista
 *                                              de inboxes pessoais em mãos;
 *                                              quando omitido, sai da `inbox`
 */
export const isAceleresPersonalConversationForUser = ({
  conversation = {},
  inbox = {},
  currentUserId = 0,
  ownsPersonalInbox = null,
} = {}) => {
  const uid = Number(currentUserId || 0);
  if (!uid) return false;

  const attributes =
    conversation.additional_attributes ||
    conversation.additionalAttributes ||
    {};
  const personalOwnerId = Number(
    attributes.personal_owner_chatwoot_user_id ||
      attributes.personalOwnerChatwootUserId ||
      0
  );

  // Primeiro: a caixa Pessoais de QUEM seria. Sem ser o dono, não é dela.
  const ownsInbox =
    ownsPersonalInbox === null
      ? isAceleresPersonalInboxOwnedBy(inbox, uid)
      : Boolean(ownsPersonalInbox);
  const isOwner = ownsInbox || personalOwnerId === uid;
  if (!isOwner) return false;

  // Depois: o servidor JÁ respondeu se a conversa ocupa a caixa.
  // `aceleres_personal_box` é `Aceleres::PersonalBox#occupied?` e vem tanto no
  // JSON da lista quanto no payload do websocket. Preferir a resposta pronta
  // é o que impede uma terceira cópia da regra de divergir da do Rails — foi
  // o que aconteceu: o backend passou a tirar de Pessoais a conversa ASSUMIDA
  // (`personal_claimed_at`, 25/08 e 02/09) e este espelho não soube. Uma
  // conversa já assumida continuava listada em Pessoais, o vendedor clicava
  // "Mover para meus" de novo e nada acontecia — o carimbo já existia e o lead
  // do funil já tinha nascido na primeira vez.
  const serverAnswer =
    conversation.aceleres_personal_box ?? conversation.aceleresPersonalBox;
  if (serverAnswer !== undefined && serverAnswer !== null) {
    return Boolean(serverAnswer);
  }

  // Fallback local, só para payload sem o campo (evento antigo, item vindo de
  // um cache anterior a esta versão). Espelha `#occupied?` na mesma ordem:
  //   1. assumida nunca volta para Pessoais;
  //   2. sem responsável, continua na caixa;
  //   3. de outra pessoa, saiu na hora;
  //   4. do próprio dono, sai ao ser iniciada.
  if (attributes.personal_claimed_at || attributes.personalClaimedAt) {
    return false;
  }

  const assigneeId = Number(
    conversation?.meta?.assignee?.id || conversation?.assignee_id || 0
  );
  if (assigneeId === 0) return true;

  return assigneeId === uid && !isAceleresConversationStarted(conversation);
};
